import { useTranslation } from 'react-i18next'
import {
  MANUAL_MACHINERY_STANDARD_TYPES,
  type ManualMachineryStandardType,
} from '@/shared/hooks/api/userStandards'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

type MachineryStandardTypePickerProps = {
  value: ManualMachineryStandardType | null | undefined
  onChange: (type: ManualMachineryStandardType | undefined) => void
  disabled?: boolean
  name?: string
}

const NOT_SPECIFIED_VALUE = 'not-specified'

const STANDARD_TYPE_LABEL_KEYS: Record<
  ManualMachineryStandardType,
  'createNew.standardTypeB' | 'createNew.standardTypeC'
> = {
  B: 'createNew.standardTypeB',
  C: 'createNew.standardTypeC',
}

export function MachineryStandardTypePicker({
  value,
  onChange,
  disabled = false,
  name = 'machinery-standard-type',
}: MachineryStandardTypePickerProps) {
  const { t } = useTranslation('standardsLibrary')

  return (
    <div>
      <span className='mb-2 block text-sm font-medium'>
        {t('createNew.standardType')}
      </span>
      <RadioGroup
        value={value ?? NOT_SPECIFIED_VALUE}
        onValueChange={(nextValue) => {
          if (nextValue === NOT_SPECIFIED_VALUE) {
            onChange(undefined)
            return
          }
          onChange(nextValue as ManualMachineryStandardType)
        }}
        disabled={disabled}
        className='flex flex-col gap-2'
      >
        <div className='flex items-center gap-2'>
          <RadioGroupItem
            value={NOT_SPECIFIED_VALUE}
            id={`${name}-${NOT_SPECIFIED_VALUE}`}
          />
          <Label
            htmlFor={`${name}-${NOT_SPECIFIED_VALUE}`}
            className='cursor-pointer text-sm font-normal'
          >
            {t('createNew.standardTypeNotSpecified')}
          </Label>
        </div>
        {MANUAL_MACHINERY_STANDARD_TYPES.map((standardType) => {
          const itemId = `${name}-${standardType}`
          return (
            <div key={standardType} className='flex items-center gap-2'>
              <RadioGroupItem value={standardType} id={itemId} />
              <Label
                htmlFor={itemId}
                className='cursor-pointer text-sm font-normal'
              >
                {t(STANDARD_TYPE_LABEL_KEYS[standardType])}
              </Label>
            </div>
          )
        })}
      </RadioGroup>
    </div>
  )
}
